/**
 * Built-in Validator Plugins
 * 
 * These validators are registered by default and check for
 * common issues with waypoint data.
 */

import { pluginRegistry } from './PluginRegistry.js';
import { validateWaypoint, calculateDistance } from '../services/WaypointService.js';
import { createIKSolver, computeAllIKSolutions } from '../services/IKService.js';

/**
 * Basic Waypoint Structure Validator
 */
export const waypointStructureValidator = {
  id: 'waypoint-structure',
  name: 'Waypoint Structure',
  description: 'Validates that waypoints have required fields with valid values',
  severity: 'error',
  
  validate: (data) => {
    const messages = [];
    const { waypoints = [] } = data;
    
    waypoints.forEach((wp, index) => {
      const result = validateWaypoint(wp);
      if (!result.valid) {
        messages.push(`Waypoint ${index + 1}: ${result.errors.join(', ')}`);
      }
    });
    
    return {
      valid: messages.length === 0,
      messages
    };
  }
};

/**
 * Reachability Validator - Uses IK solver to check if positions are actually reachable
 */
export const reachabilityValidator = {
  id: 'reachability',
  name: 'Robot Reachability (IK)',
  description: 'Uses inverse kinematics to verify waypoints are reachable by the robot',
  severity: 'error',
  
  validate: (data, context = {}) => {
    const messages = [];
    const { waypoints = [] } = data;
    const robotType = data.robotType || context.robotType || 'ur10e';
    const robotName = data.robotName || context.robotName || robotType.toUpperCase();
    
    // Create IK solver for the robot
    const { solver, error: solverError } = createIKSolver(robotType);
    
    if (solverError || !solver) {
      return {
        valid: false,
        messages: [`Could not create IK solver for ${robotName}: ${solverError || 'Unknown error'}`]
      };
    }
    
    // Check each waypoint
    waypoints.forEach((wp, index) => {
      if (!wp.position || !wp.rotation) return;
      
      const targetPose = {
        position: { ...wp.position },
        rotation: { ...wp.rotation }
      };
      
      try {
        // Try to find any valid IK solution
        const solutions = computeAllIKSolutions(solver, targetPose, null);
        
        if (solutions.length === 0) {
          // No valid solutions found - waypoint is unreachable
          const distance = Math.sqrt(
            wp.position.x ** 2 + 
            wp.position.y ** 2 + 
            wp.position.z ** 2
          );
          messages.push(
            `Waypoint ${index + 1} is unreachable by ${robotName} (${(distance * 1000).toFixed(0)}mm from base) - no valid IK solution`
          );
        }
      } catch (err) {
        messages.push(
          `Waypoint ${index + 1}: IK computation error - ${err.message}`
        );
      }
    });
    
    return {
      valid: messages.length === 0,
      messages
    };
  }
};

/**
 * Path Continuity Validator - Checks for large jumps between waypoints
 */
export const pathContinuityValidator = {
  id: 'path-continuity',
  name: 'Path Continuity',
  description: 'Warns about large jumps between consecutive waypoints',
  severity: 'warning',
  
  validate: (data, context = {}) => {
    const messages = [];
    const { waypoints = [] } = data;
    const { maxJump = 0.5 } = context; // Default max jump of 500mm
    
    for (let i = 1; i < waypoints.length; i++) {
      const prev = waypoints[i - 1];
      const curr = waypoints[i];
      
      const distance = calculateDistance(prev.position, curr.position);
      
      if (distance > maxJump) {
        messages.push(
          `Large jump between waypoints ${i} and ${i + 1}: ${(distance * 1000).toFixed(0)}mm`
        );
      }
    }
    
    return {
      valid: messages.length === 0,
      messages
    };
  }
};

/**
 * Feature Reference Validator - Checks if all referenced features exist
 */
export const featureReferenceValidator = {
  id: 'feature-reference',
  name: 'Feature References',
  description: 'Validates that all waypoints reference existing features',
  severity: 'error',
  
  validate: (data) => {
    const messages = [];
    const { waypoints = [], features = [] } = data;
    
    const featureNames = new Set(features.map(f => f.name));
    
    waypoints.forEach((wp, index) => {
      if (wp.feature && !featureNames.has(wp.feature)) {
        messages.push(
          `Waypoint ${index + 1} references unknown feature: "${wp.feature}"`
        );
      }
    });
    
    return {
      valid: messages.length === 0,
      messages
    };
  }
};

/**
 * Duplicate Position Validator - Checks for waypoints at the same position
 */
export const duplicatePositionValidator = {
  id: 'duplicate-position',
  name: 'Duplicate Positions',
  description: 'Warns about waypoints at the same or very similar positions',
  severity: 'info',
  
  validate: (data, context = {}) => {
    const messages = [];
    const { waypoints = [] } = data;
    const { tolerance = 0.001 } = context; // 1mm default tolerance
    
    for (let i = 0; i < waypoints.length; i++) {
      for (let j = i + 1; j < waypoints.length; j++) {
        const distance = calculateDistance(
          waypoints[i].position, 
          waypoints[j].position
        );
        
        if (distance < tolerance) {
          messages.push(
            `Waypoints ${i + 1} and ${j + 1} are at the same position`
          );
        }
      }
    }
    
    return {
      valid: messages.length === 0,
      messages
    };
  }
};

/**
 * Register all built-in validators
 */
export function registerBuiltInValidators() {
  pluginRegistry.registerValidator(waypointStructureValidator);
  pluginRegistry.registerValidator(reachabilityValidator);
  pluginRegistry.registerValidator(featureReferenceValidator);
  pluginRegistry.registerValidator(duplicatePositionValidator);
}
